import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Alert, Paper, Step, StepLabel, Stepper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSubmission } from 'services/Data/SubmissionServices';
import { useCriteriaTypes, useCriteriaTypeDetails } from 'services/Data/CriteriaServices';
import { useCriteriaOptions } from 'services/Data/CriteriaOptionServices';
import { translateLabel } from 'services/Utilities/labelUtils';
import { CriteriaTypeDetailTypeEnumValue } from 'enums/CriteriaTypeDetailTypeEnum';
import { CertificationStatusEnumValue } from 'enums/CertificationStatusEnum';
import { detailAppliesToSubmission, orderDetailsByHierarchy } from 'services/Utilities/criteriaDetailUtils';

const hasNonEmptyValue = value => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

// Wizard stepper for submission flow (hero -> criteria option -> photos -> summary)
const WizardView = () => {
  const { submissionId, stepNumber } = useParams();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentLanguage = i18n.language;
  const basePath = '/dealer/certification/submissions';

  const { data: submission } = useSubmission(submissionId);
  const { data: allCriteriaTypes } = useCriteriaTypes();
  const { data: allCriteriaTypeDetails } = useCriteriaTypeDetails(submissionId);
  const { data: criteriaOptions } = useCriteriaOptions();

  const currentStep = parseInt(stepNumber, 10) || 1;
  const submissionCriteriaId = submission?.criteria_Id || null;
  const shouldSkipHeroStep = submission?.criteria?.isHeroUnitVisible === false;
  const requiresHeroUnitSelection = !shouldSkipHeroStep;

  useEffect(() => {
    if (!shouldSkipHeroStep) return;
    if (currentStep === 1) {
      history.replace(`${basePath}/${submissionId}/step/2`);
    }
  }, [basePath, currentStep, history, shouldSkipHeroStep, submissionId]);

  const heroUnitProductLineId = useMemo(() => {
    if (!submission) return null;
    return submission.heroUnitProductLine_Id || submission.heroUnitProductLine?.id || null;
  }, [submission]);

  const dealerProductLineIds = useMemo(
    () => submission?.dealer?.dealerProductLines?.map(line => line.id).filter(Boolean) || [],
    [submission?.dealer?.dealerProductLines],
  );

  const submissionDetailValueByDetailId = useMemo(() => {
    const map = new Map();
    submission?.submissionDetails?.forEach(detail => {
      if (!detail?.criteriaTypeDetail_Id) return;
      map.set(detail.criteriaTypeDetail_Id, detail.value ?? '');
    });
    return map;
  }, [submission?.submissionDetails]);

  const isRequiredPhotoDetail = useCallback(
    detail => detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Photo && detail?.isRequired !== false,
    [],
  );

  const isRequiredInputDetail = useCallback(
    detail => detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Input && detail?.isRequired !== false,
    [],
  );

  const isRequiredSelectDetail = useCallback(
    detail => detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Dropdown && detail?.isRequired !== false,
    [],
  );

  const attemptedOptionId = submission?.attemptedCriteriaOption_Id;

  const availableCriteriaOptions = useMemo(() => {
    if (!criteriaOptions || !submissionCriteriaId) return [];
    return criteriaOptions.filter(option => {
      if (!option?.isAvailableForDealerSelection) return false;
      const matchesJoin = (option.criteriaCriteriaOptions || []).some(join => join?.criteria_Id === submissionCriteriaId);
      const matchesLegacy = option.criteria_Id === submissionCriteriaId;
      return matchesJoin || matchesLegacy;
    });
  }, [criteriaOptions, submissionCriteriaId]);

  const hasCriteriaSelectionStep = availableCriteriaOptions.length > 0;

  useEffect(() => {
    if (criteriaOptions === undefined) return;
    if (hasCriteriaSelectionStep) return;
    if (currentStep === 2) {
      history.replace(`${basePath}/${submissionId}/step/3`);
    }
  }, [basePath, criteriaOptions, currentStep, hasCriteriaSelectionStep, history, submissionId]);

  const shouldIncludeDetail = useCallback(
    detail =>
      detailAppliesToSubmission(detail, {
        attemptedCriteriaOptionId: attemptedOptionId,
        heroUnitProductLineId,
        dealerProductLineIds,
        submissionDetailValueByDetailId,
        requiresHeroUnitSelection,
        hasAvailableCriteriaOptions: hasCriteriaSelectionStep,
      }),
    [
      attemptedOptionId,
      dealerProductLineIds,
      heroUnitProductLineId,
      submissionDetailValueByDetailId,
      requiresHeroUnitSelection,
      hasCriteriaSelectionStep,
    ],
  );

  const eligibleDetailsByType = useMemo(() => {
    if (!allCriteriaTypeDetails || (requiresHeroUnitSelection && !heroUnitProductLineId)) return new Map();
    const grouped = new Map();
    allCriteriaTypeDetails.forEach(detail => {
      if (!detail?.criteriaType_Id) return;
      const existing = grouped.get(detail.criteriaType_Id) ?? [];
      existing.push(detail);
      grouped.set(detail.criteriaType_Id, existing);
    });

    const filtered = new Map();
    grouped.forEach((details, criteriaTypeId) => {
      const eligible = details.filter(detail => shouldIncludeDetail(detail));
      const ordered = orderDetailsByHierarchy(eligible);
      if (ordered.length > 0) filtered.set(criteriaTypeId, ordered);
    });
    return filtered;
  }, [allCriteriaTypeDetails, heroUnitProductLineId, requiresHeroUnitSelection, shouldIncludeDetail]);

  const eligibleCriteriaTypes = useMemo(() => {
    if (!allCriteriaTypes || !submission?.criteria_Id) return [];

    return allCriteriaTypes
      .filter(ct => ct.criteria_Id === submission.criteria_Id)
      .filter(ct => {
        const optionIds = (ct.criteriaTypeCriteriaOptions || []).map(join => join?.criteriaOption_Id);
        if (attemptedOptionId && !optionIds.includes(attemptedOptionId)) return false;

        const productLineIds = (ct.criteriaTypeProductLines || []).map(join => join?.productLine_Id);
        if (requiresHeroUnitSelection) {
          if (!heroUnitProductLineId || !productLineIds.includes(heroUnitProductLineId)) return false;
        } else if (heroUnitProductLineId && !productLineIds.includes(heroUnitProductLineId)) return false;

        const dealerLines = (ct.criteriaTypeDealerProductLines || []).map(join => join?.productLine_Id) || [];
        if (dealerLines.length && !dealerLines.some(id => dealerProductLineIds.includes(id))) return false;

        return true;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [
    allCriteriaTypes,
    submission?.criteria_Id,
    attemptedOptionId,
    heroUnitProductLineId,
    requiresHeroUnitSelection,
    dealerProductLineIds,
  ]);

  const eligibleCriteriaTypeIds = useMemo(() => new Set(eligibleCriteriaTypes.map(ct => ct.id)), [eligibleCriteriaTypes]);

  const buildRequiredDetailIds = useCallback(
    predicate => {
      const ids = new Set();
      eligibleDetailsByType.forEach((details, criteriaTypeId) => {
        if (!eligibleCriteriaTypeIds.has(criteriaTypeId)) return;
        details.forEach(detail => {
          if (predicate(detail)) ids.add(detail.id);
        });
      });
      return ids;
    },
    [eligibleCriteriaTypeIds, eligibleDetailsByType],
  );

  const eligiblePhotoDetailIds = useMemo(() => buildRequiredDetailIds(isRequiredPhotoDetail), [
    buildRequiredDetailIds,
    isRequiredPhotoDetail,
  ]);

  const eligibleInputDetailIds = useMemo(() => buildRequiredDetailIds(isRequiredInputDetail), [
    buildRequiredDetailIds,
    isRequiredInputDetail,
  ]);

  const eligibleSelectDetailIds = useMemo(() => buildRequiredDetailIds(isRequiredSelectDetail), [
    buildRequiredDetailIds,
    isRequiredSelectDetail,
  ]);

  const submissionDetailsByDetailId = useMemo(() => {
    const map = new Map();
    submission?.submissionDetails?.forEach(sd => {
      if (!map.has(sd.criteriaTypeDetail_Id)) {
        map.set(sd.criteriaTypeDetail_Id, sd);
      }
    });
    return map;
  }, [submission?.submissionDetails]);

  const buildCompletedDetailIds = useCallback(
    (requiredIds, completionPredicate) => {
      const completed = new Set();
      requiredIds.forEach(detailId => {
        const submissionDetail = submissionDetailsByDetailId.get(detailId);
        if (completionPredicate(submissionDetail)) {
          completed.add(detailId);
        }
      });
      return completed;
    },
    [submissionDetailsByDetailId],
  );

  const completedPhotoDetailIds = useMemo(
    () => buildCompletedDetailIds(eligiblePhotoDetailIds, sd => sd?.files && sd.files.length > 0),
    [buildCompletedDetailIds, eligiblePhotoDetailIds],
  );

  const completedInputDetailIds = useMemo(
    () => buildCompletedDetailIds(eligibleInputDetailIds, sd => hasNonEmptyValue(sd?.value)),
    [buildCompletedDetailIds, eligibleInputDetailIds],
  );

  const completedSelectDetailIds = useMemo(
    () => buildCompletedDetailIds(eligibleSelectDetailIds, sd => hasNonEmptyValue(sd?.value)),
    [buildCompletedDetailIds, eligibleSelectDetailIds],
  );

  const uploadedPhotoCount = completedPhotoDetailIds.size;

  const wizardSteps = useMemo(() => {
    const steps = [];

    if (submission?.criteria?.isHeroUnitVisible) {
      steps.push({
        id: 'step1',
        routeStep: 1,
        name: t('heroUnit.selectYourHeroUnit'),
        type: 'heroUnit',
        selectedProductLine: submission?.heroUnitProductLine,
      });
    }

    if (hasCriteriaSelectionStep) {
      steps.push({
        id: 'step2',
        routeStep: 2,
        name: t('criteriaOption.selectYourLevel'),
        type: 'selection',
        selectedOption: submission?.attemptedCriteriaOption,
      });
    }

    steps.push({
      id: 'step3',
      routeStep: 3,
      name: t('submission.uploadPhotos'),
      type: 'criteria',
      uploaded: uploadedPhotoCount,
      total: eligiblePhotoDetailIds.size,
    });

    steps.push({
      id: 'step4',
      routeStep: 4,
      name: t('submission.summary'),
      type: 'summary',
    });

    return steps;
  }, [submission, hasCriteriaSelectionStep, uploadedPhotoCount, eligiblePhotoDetailIds.size, t]);

  const hasHeroUnit = requiresHeroUnitSelection ? !!submission?.heroUnitProductLine_Id : true;
  const requiresCriteriaSelection = hasCriteriaSelectionStep;
  const hasCriteriaOption = !!submission?.attemptedCriteriaOption_Id;
  const hasCriteriaOptionOrIsOptional = hasCriteriaOption || !requiresCriteriaSelection;
  const canAccessPhotoStep = hasHeroUnit && hasCriteriaOptionOrIsOptional;
  const hasCompletedAllRequiredPhotos =
    eligiblePhotoDetailIds.size === 0 || completedPhotoDetailIds.size === eligiblePhotoDetailIds.size;
  const hasCompletedAllRequiredInputs =
    eligibleInputDetailIds.size === 0 || completedInputDetailIds.size === eligibleInputDetailIds.size;
  const hasCompletedAllRequiredSelects =
    eligibleSelectDetailIds.size === 0 || completedSelectDetailIds.size === eligibleSelectDetailIds.size;
  const hasCompletedAllRequirements =
    hasCompletedAllRequiredPhotos && hasCompletedAllRequiredInputs && hasCompletedAllRequiredSelects;
  const isCertified = submission?.status === CertificationStatusEnumValue.Passed;
  const pastQuarter = submission?.quarter?.endDate ? new Date(submission.quarter.endDate) <= new Date() : false;
  const canAccessSummaryStep = canAccessPhotoStep && (hasCompletedAllRequirements || isCertified || pastQuarter);

  useEffect(() => {
    if (!submission) return;
    if (currentStep >= 4 && !canAccessSummaryStep) {
      history.replace(`${basePath}/${submissionId}/step/3`);
    }
  }, [basePath, canAccessSummaryStep, currentStep, history, submission, submissionId]);

  const isStepClickable = useCallback(
    routeStep => {
      if (routeStep === 1) return true;
      if (routeStep === 2) return requiresCriteriaSelection && hasHeroUnit;
      if (routeStep === 3) return canAccessPhotoStep;
      if (routeStep >= 4) return canAccessSummaryStep;
      return false;
    },
    [requiresCriteriaSelection, hasHeroUnit, canAccessPhotoStep, canAccessSummaryStep],
  );

  const getStepLink = useCallback(
    routeStep => {
      if (!isStepClickable(routeStep)) return null;
      return `${basePath}/${submissionId}/step/${routeStep}`;
    },
    [basePath, isStepClickable, submissionId],
  );

  const handleStepClick = useCallback(
    routeStep => {
      const link = getStepLink(routeStep);
      if (link) {
        history.push(link);
      }
    },
    [getStepLink, history],
  );

  const activeStepIndex = wizardSteps.findIndex(step => step.routeStep === currentStep);
  const safeActiveStepIndex = activeStepIndex >= 0 ? activeStepIndex : 0;

  const renderStepOptional = step => {
    const baseStyles = {
      display: 'block',
      mt: 0.5,
      textAlign: isMobile ? 'left' : 'center',
      fontWeight: 600,
    };

    if (step.type === 'heroUnit' && step.selectedProductLine) {
      return (
        <Typography variant="h2" sx={{ ...baseStyles }}>
          {translateLabel(step.selectedProductLine.name, currentLanguage)}
        </Typography>
      );
    }

    if (step.type === 'selection' && step.selectedOption) {
      return (
        <Typography variant="h2" sx={{ ...baseStyles }}>
          {translateLabel(step.selectedOption.name, currentLanguage)}
        </Typography>
      );
    }

    if (step.type === 'criteria') {
      const isComplete = step.uploaded === step.total && step.total > 0;
      return (
        <Typography
          variant="caption"
          sx={{
            ...baseStyles,
            color: isComplete ? theme.palette.success.main : theme.palette.text.secondary,
          }}>
          <Box
            component="span"
            sx={{ fontWeight: 700, color: isComplete ? theme.palette.success.main : theme.palette.text.secondary }}>
            {step.uploaded}
          </Box>
          /{step.total} {t('submission.requiredPhotosSubmitted')}
        </Typography>
      );
    }

    if (step.type === 'summary') {
      return (
        <Typography variant="caption" sx={{ ...baseStyles, color: theme.palette.text.secondary }}>
          {t('submission.finalStep')}
        </Typography>
      );
    }

    return null;
  };

  if (!submission) return null;

  return (
    <Paper
      data-tour="submissionWizard"
      elevation={4}
      sx={{
        bgcolor: theme.palette.background.paper,
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: { xs: 2, md: 3 },
        mb: { xs: 3, md: 4 },
        boxShadow: theme => theme.shadows[4],
      }}>
      {wizardSteps.length === 0 ? (
        <Alert severity="info">{t('submission.noCriteriaTypes')}</Alert>
      ) : (
        <Stepper
          activeStep={safeActiveStepIndex}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          alternativeLabel={!isMobile}
          sx={{
            display: 'flex',
            justifyContent: isMobile ? 'flex-start' : 'center',
            alignItems: isMobile ? 'stretch' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0,
            '& .MuiStepLabel-root .Mui-completed': {
              color: theme.palette.primary.main,
            },
            '& .MuiStepLabel-root .Mui-active': {
              color: theme.palette.text.secondary,
            },
            '& .MuiStepLabel-label': {
              color: theme.palette.text.primary,
              textAlign: isMobile ? 'left' : 'center',
              fontSize: isMobile ? '0.85rem' : '1rem',
              '&.Mui-active': {
                color: theme.palette.primary.main,
                fontWeight: 700,
              },
              '&.Mui-completed': {
                color: theme.palette.text.disabled,
              },
            },
            '& .MuiStepIcon-root': {
              color: theme.palette.action.disabled,
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              '&.Mui-active': {
                color: theme.palette.primary.main,
              },
              '&.Mui-completed': {
                color: theme.palette.primary.main,
              },
            },
            '& .MuiStepConnector-line': {
              borderColor: theme.palette.divider,
              minHeight: isMobile ? 24 : 'auto',
            },
            '& .MuiStep-root': {
              width: isMobile ? '100%' : 'auto',
            },
          }}>
          {wizardSteps.map(step => {
            const stepLink = getStepLink(step.routeStep);
            const isClickable = isStepClickable(step.routeStep);
            return (
              <Step
                key={step.id}
                sx={{ cursor: isClickable ? 'pointer' : 'default' }}
                onClick={() => handleStepClick(step.routeStep)}>
                <StepLabel
                  component={stepLink ? Link : 'div'}
                  to={stepLink || undefined}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    width: isMobile ? '100%' : 'auto',
                    '&:hover': stepLink
                      ? {
                          '& .MuiStepLabel-label': {
                            color: theme.palette.text.primary,
                          },
                        }
                      : {},
                    '& .MuiStepLabel-iconContainer': {
                      pr: isMobile ? 2 : 0,
                    },
                  }}
                  optional={renderStepOptional(step)}>
                  {step.name}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      )}
    </Paper>
  );
};

export default WizardView;
